import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import {
  IActionMenuItems,
  MainApiService,
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
  useRole,
} from "@4uhub/lib4uhub";
import { useConfirm } from "material-ui-confirm";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import { GlobalAxios } from "../../../axios/axios-config";
import { IEvaluationForms } from "../../../models/evaluation-forms";
import { Chip } from "@mui/material";
import {
  clone as cloneEvaluationForm,
  getEvaluationFormById,
  release,
  undoRelease,
} from "../../../services/evaluationForm.service";

const translationPath = "page.evaluation.forms.";

const FORMS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/EvaluationForm/";

const formsService = new MainApiService<IEvaluationForms>(
  FORMS_ROUTE,
  GlobalAxios,
);

const FormsPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const createPermission = useRole(["Create"]);
  const updatePermission = useRole(["Update"]);

  const { t } = useTranslation();
  const { setMessage } = useNotificationContext();

  const [refresh, setRefresh] = useState(0);

  const { sendRequest: getForm, loading: getLoading } = useFetch(
    getEvaluationFormById,
  );
  const { sendRequest: releaseForm, loading: releaseLoading } =
    useFetch(release);
  const { sendRequest: undoReleaseForm, loading: undoReleaseLoading } =
    useFetch(undoRelease);
  const { sendRequest: cloneForm, loading: cloneLoading } =
    useFetch(cloneEvaluationForm);

  const columns: GridColDef<IEvaluationForms>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: t(translationPath + "title") || "Title",
        renderCell: (params) => {
          return params.value ? (
            <OverflowTooltip
              text={params.value}
              tooltip={params.value}
              textTransform="none"
              variant="body2"
            />
          ) : (
            "-"
          );
        },
      },
      {
        field: "description",
        headerName: t(translationPath + "description") || "Description",
        renderCell: (params) => {
          return params.value ? (
            <OverflowTooltip
              text={params.value}
              tooltip={params.value}
              textTransform="none"
              variant="body2"
            />
          ) : (
            "-"
          );
        },
      },
      {
        field: "enable",
        headerName: t(translationPath + "status") || "Status",
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.value ? "success" : "error"}
          />
        ),
      },
      {
        field: "released",
        headerName: t(translationPath + "release_status") || "Release status",
        renderCell: (params) => (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "released")
                : t(translationPath + "awaiting_release")
            }
            color={params.value ? "success" : "warning"}
          />
        ),
      },
    ],
    [t],
  );

  const handleRowClick = (params: GridRowParams<IEvaluationForms>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  const refreshList = useCallback(() => {
    setRefresh((current) => current + 1);
  }, []);

  const handleRelease = useCallback(
    async (form: IEvaluationForms) => {
      const { data, success: getSuccess } = await getForm(form.id);

      if (!getSuccess || !data) return;

      if (data.evaluationFormQuestions.length === 0) {
        setMessage({
          message: t(translationPath + "release_requires_question"),
          type: "warning",
        });
        return;
      }

      try {
        await confirm({
          title: t(translationPath + "release_confirm.title"),
          description: t(translationPath + "release_confirm.description"),
          cancellationText: t(translationPath + "cancel_action"),
          confirmationText: t(translationPath + "continue_action"),
          confirmationButtonProps: { variant: "contained" },
        });

        const { success } = await releaseForm(form.id);
        if (success) refreshList();
      } catch (error) {
        // The confirmation dialog was cancelled.
      }
    },
    [confirm, getForm, refreshList, releaseForm, setMessage, t],
  );

  const handleUndoRelease = useCallback(
    async (form: IEvaluationForms) => {
      const { success } = await undoReleaseForm(form.id);
      if (success) refreshList();
    },
    [refreshList, undoReleaseForm],
  );

  const handleClone = useCallback(
    async (form: IEvaluationForms) => {
      try {
        await confirm({
          title: t(translationPath + "clone_confirm.title"),
          description: t(translationPath + "clone_confirm.description"),
          cancellationText: t(translationPath + "cancel_action"),
          confirmationText: t(translationPath + "continue_action"),
          confirmationButtonProps: { variant: "contained" },
        });

        const { success } = await cloneForm(form.id);
        if (success) refreshList();
      } catch (error) {
        // The confirmation dialog was cancelled.
      }
    },
    [cloneForm, confirm, refreshList, t],
  );

  const rowActions = useCallback(
    ({ row }: GridRowParams<IEvaluationForms>) => {
      const actions: IActionMenuItems<IEvaluationForms>[] = [];

      if (updatePermission && !row.released) {
        actions.push({
          action: () => handleRelease(row),
          icon: <LockIcon fontSize="small" />,
          text: t(translationPath + "release_form"),
        });
      }

      if (updatePermission && row.released) {
        actions.push({
          action: () => handleUndoRelease(row),
          icon: <LockOpenIcon fontSize="small" />,
          text: t(translationPath + "undo_release"),
        });
      }

      if (createPermission) {
        actions.push({
          action: () => handleClone(row),
          icon: <ContentCopyIcon fontSize="small" />,
          text: t(translationPath + "clone_form"),
        });
      }

      return actions;
    },
    [
      createPermission,
      handleClone,
      handleRelease,
      handleUndoRelease,
      t,
      updatePermission,
    ],
  );

  const serviceProps = useMemo(
    () => ({ refresh: refresh || undefined }),
    [refresh],
  );

  return (
    <Table<IEvaluationForms>
      showDefaultMacroFilters={false}
      service={formsService}
      columns={columns}
      title={t(translationPath + "forms") || "Forms"}
      searchInput
      onAdd={handleAdd}
      showButton={createPermission}
      onRowClick={handleRowClick}
      rowActions={rowActions}
      serviceProps={serviceProps}
      syncing={
        getLoading || releaseLoading || undoReleaseLoading || cloneLoading
      }
      searchInputPlaceHolder={
        t(translationPath + "search") || "Searh for a form"
      }
      addButtonLabel={t(translationPath + "add_form") || "Add Form"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default FormsPage;
